import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useSnackbar } from 'notistack';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';

import useAuth from '../../auth/useAuth';
import useCart from '../../cart/useCart';
import quotesApi from '../../api/quotesApi';
import { getErrorMessage, getBlobErrorMessage } from '../../utils/errorHandler';
import { triggerBlobDownload } from '../../utils/downloadFile';
import ProductImagePlaceholder from '../../sections/client/products/ProductImagePlaceholder';
import QuantityStepper from '../../sections/client/products/QuantityStepper';

// Miniatura cuadrada reutilizada en la lista de busqueda y en "Tu cotizacion".
function ProductThumbnail({ product, size }) {
  return (
    <Box sx={{ width: size, height: size, flexShrink: 0, borderRadius: 1, overflow: 'hidden' }}>
      {product.image_url ? (
        <Box
          component="img"
          src={product.image_url}
          alt={product.name}
          sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', bgcolor: 'background.paper' }}
        />
      ) : (
        <ProductImagePlaceholder height="100%" iconFontSize={size / 2.5} />
      )}
    </Box>
  );
}

const currencyPen = new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' });
const currencyUsd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const schema = yup.object({
  customer_name: yup.string().trim().required('Ingresa tu nombre'),
  customer_email: yup.string().trim().email('Ingresa un correo válido').required('Ingresa tu correo'),
  customer_phone: yup.string().nullable(),
});

export default function QuoteBuilderPage() {
  const { user } = useAuth();
  const { addItem: addToCart, openDrawer } = useCart();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [items, setItems] = useState([]);
  const [submittedQuote, setSubmittedQuote] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      customer_name: user?.name || '',
      customer_email: user?.email || '',
      customer_phone: user?.phone || '',
    },
  });

  useEffect(() => {
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await quotesApi.searchCatalogProducts(searchInput);
        setSearchResults(data.data);
      } catch (error) {
        // Busqueda secundaria: si falla no se interrumpe el armado de la cotizacion.
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  const addItem = (product) => {
    setItems((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const updateQuantity = (productId, quantity) => {
    const parsed = Math.max(1, Number(quantity) || 1);
    setItems((prev) => prev.map((item) => (item.product.id === productId ? { ...item, quantity: parsed } : item)));
  };

  const removeItem = (productId) => {
    setItems((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const totalPen = items.reduce((sum, item) => sum + item.product.price_pen * item.quantity, 0);
  const totalUsd = items.reduce((sum, item) => sum + item.product.price_usd * item.quantity, 0);

  const onSubmit = async (data) => {
    if (items.length === 0) {
      enqueueSnackbar('Agrega al menos un producto a tu cotización.', { variant: 'warning' });
      return;
    }

    try {
      const { data: response } = await quotesApi.createQuote({
        ...data,
        items: items.map((item) => ({ product_id: item.product.id, quantity: item.quantity })),
      });
      setSubmittedQuote(response.data);
      setItems([]);
    } catch (error) {
      enqueueSnackbar(getErrorMessage(error, 'No se pudo enviar tu cotización.'), { variant: 'error' });
    }
  };

  // "Comprar": lleva la lista armada directo al carrito/checkout, sin pasar por el envio
  // de la cotizacion por correo (ese es el flujo de "Enviar cotizacion" de abajo).
  const handleBuyNow = () => {
    if (items.length === 0) {
      enqueueSnackbar('Agrega al menos un producto a tu cotización.', { variant: 'warning' });
      return;
    }

    items.forEach((item) => addToCart(item.product, item.quantity));
    openDrawer();
    navigate('/checkout');
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const response = await quotesApi.downloadGuestQuotePdf(submittedQuote.uuid);
      triggerBlobDownload(response.data, `cotizacion-${submittedQuote.uuid}.pdf`);
    } catch (error) {
      enqueueSnackbar(await getBlobErrorMessage(error, 'No se pudo descargar el PDF.'), { variant: 'error' });
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (submittedQuote) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Card>
          <CardContent>
            <Stack spacing={2} sx={{ alignItems: 'center', textAlign: 'center', py: 3 }}>
              <Typography variant="h5">¡Cotización enviada!</Typography>
              <Typography color="text.secondary">
                Registramos tu cotización N.° {submittedQuote.id}. Nuestro equipo se pondrá en contacto contigo.
              </Typography>
              <Typography variant="h6">
                Total: {currencyPen.format(submittedQuote.subtotal_pen)} / {currencyUsd.format(submittedQuote.subtotal_usd)}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfOutlinedIcon />}
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                >
                  {downloadingPdf ? 'Descargando...' : 'Descargar PDF'}
                </Button>
                <Button variant="contained" onClick={() => setSubmittedQuote(null)}>
                  Crear otra cotización
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 5 }}>
      <Stack spacing={1} sx={{ mb: 4 }}>
        <Typography variant="h4">Cotizador de armado de PC</Typography>
        <Typography color="text.secondary">
          Busca productos, arma tu lista y envíanos tus datos. Te contactaremos con tu cotización en soles y dólares.
        </Typography>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <TextField
                fullWidth
                label="Buscar producto"
                placeholder="Elige el hardware"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1 }} />,
                  },
                }}
              />

              {!searching && searchInput && searchResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  No encontramos productos con ese criterio.
                </Typography>
              )}

              <List>
                {searchResults.map((product) => (
                  <ListItem
                    key={product.id}
                    secondaryAction={
                      <IconButton edge="end" color="primary" onClick={() => addItem(product)}>
                        <AddIcon />
                      </IconButton>
                    }
                  >
                    <ListItemAvatar>
                      <ProductThumbnail product={product} size={48} />
                    </ListItemAvatar>
                    <ListItemText
                      primary={product.name}
                      secondary={`${currencyPen.format(product.price_pen)} / ${currencyUsd.format(product.price_usd)}`}
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tu cotización
              </Typography>

              {items.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aún no agregaste productos.
                </Typography>
              ) : (
                <Stack divider={<Divider />} spacing={1.5}>
                  {items.map((item) => (
                    <Stack sx={{ alignItems: 'center' }} key={item.product.id} direction="row" spacing={2}>
                      <ProductThumbnail product={item.product} size={56} />
                      <Stack sx={{ flexGrow: 1 }}>
                        <Typography variant="body2">{item.product.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {currencyPen.format(item.product.price_pen)} / {currencyUsd.format(item.product.price_usd)}{' '}
                          c/u
                        </Typography>
                      </Stack>
                      <QuantityStepper
                        value={item.quantity}
                        onChange={(quantity) => updateQuantity(item.product.id, quantity)}
                      />
                      <IconButton onClick={() => removeItem(item.product.id)} size="small">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>
              )}

              {items.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Stack direction="row" sx={{ justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Total</Typography>
                    <Typography variant="h6">
                      {currencyPen.format(totalPen)} / {currencyUsd.format(totalUsd)}
                    </Typography>
                  </Stack>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ShoppingCartOutlinedIcon />}
                    onClick={handleBuyNow}
                  >
                    Comprar
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tus datos
              </Typography>
              <Stack component="form" spacing={2.5} onSubmit={handleSubmit(onSubmit)} noValidate>
                {items.length === 0 && (
                  <Alert severity="info">Agrega al menos un producto antes de enviar tu cotización.</Alert>
                )}

                <TextField
                  label="Nombre completo"
                  fullWidth
                  error={Boolean(errors.customer_name)}
                  helperText={errors.customer_name?.message}
                  {...register('customer_name')}
                />
                <TextField
                  label="Correo electrónico"
                  type="email"
                  fullWidth
                  error={Boolean(errors.customer_email)}
                  helperText={errors.customer_email?.message}
                  {...register('customer_email')}
                />
                <TextField
                  label="Teléfono (opcional)"
                  fullWidth
                  error={Boolean(errors.customer_phone)}
                  helperText={errors.customer_phone?.message}
                  {...register('customer_phone')}
                />

                <Button type="submit" variant="contained" size="large" disabled={isSubmitting || items.length === 0}>
                  {isSubmitting ? 'Enviando...' : 'Enviar cotización'}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
